import type {
  CompanionPageId,
  CompanionPanelConversationDto,
  CompanionPanelEvent,
  CompanionPanelTurnDto,
} from '@fluenceur/types';

/**
 * The Companion's answer arrives as Server-Sent Events from the POST that asked
 * the question. Nothing here renders: frames are parsed into events, and each
 * event is folded into the conversation held by the query cache, which is the
 * one thing the thread reads.
 */

const EVENT_TYPES: ReadonlySet<string> = new Set<CompanionPanelEvent['type']>([
  'turn',
  'step_started',
  'step_ended',
  'text_delta',
  'draft',
  'done',
]);

const toEvent = (data: string): CompanionPanelEvent | null => {
  try {
    const value: unknown = JSON.parse(data);
    if (typeof value !== 'object' || value === null) return null;
    const type = (value as { type?: unknown }).type;
    return typeof type === 'string' && EVENT_TYPES.has(type) ? (value as CompanionPanelEvent) : null;
  } catch {
    return null;
  }
};

/**
 * Splits a byte-decoded stream into frames. A frame is complete at its blank
 * line; comment lines (`: keep-alive`) and unknown fields are ignored, and a
 * frame that is not a panel event is dropped rather than failing the answer.
 */
export function createPanelEventParser(onEvent: (event: CompanionPanelEvent) => void): {
  push: (chunk: string) => void;
  /** End of stream: a last frame without its blank line still counts. */
  flush: () => void;
} {
  let buffer = '';

  const emit = (frame: string) => {
    const data = frame
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''))
      .join('\n');
    const event = data ? toEvent(data) : null;
    if (event) onEvent(event);
  };

  return {
    push: (chunk) => {
      buffer += chunk.replace(/\r\n?/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        emit(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');
      }
    },
    flush: () => {
      if (buffer.trim()) emit(buffer);
      buffer = '';
    },
  };
}

/** The question shown at once, before the server has even named the turn. */
export function appendPendingTurn(
  conversation: CompanionPanelConversationDto | null | undefined,
  input: { pendingId: string; question: string; pageId: CompanionPageId; now: string }
): CompanionPanelConversationDto {
  const turn: CompanionPanelTurnDto = {
    id: input.pendingId,
    question: input.question,
    pageId: input.pageId,
    status: 'running',
    steps: [],
    answerText: '',
    preparationId: null,
    sources: [],
    startedAt: input.now,
    completedAt: null,
  };
  if (!conversation) {
    return { id: input.pendingId, turns: [turn], lastActivityAt: input.now, resumable: false };
  }
  return { ...conversation, turns: [...conversation.turns, turn], lastActivityAt: input.now };
}

/** A question whose stream ended before the server named its turn, and that nothing settled. */
export function interruptTurn(
  conversation: CompanionPanelConversationDto,
  turnId: string
): CompanionPanelConversationDto {
  return {
    ...conversation,
    turns: conversation.turns.map((turn) =>
      turn.id === turnId && turn.status === 'running' ? { ...turn, status: 'interrupted' } : turn
    ),
  };
}

export function removeTurn(
  conversation: CompanionPanelConversationDto,
  turnId: string
): CompanionPanelConversationDto {
  return { ...conversation, turns: conversation.turns.filter((turn) => turn.id !== turnId) };
}

const applyToTurn = (
  turn: CompanionPanelTurnDto,
  event: CompanionPanelEvent,
  now: string
): CompanionPanelTurnDto => {
  switch (event.type) {
    case 'turn':
      return { ...turn, id: event.turnId };
    case 'step_started':
      return {
        ...turn,
        steps: [
          ...turn.steps,
          {
            callId: event.callId,
            tool: event.tool,
            label: event.label,
            startedAt: now,
            endedAt: null,
            outcome: null,
          },
        ],
      };
    case 'step_ended':
      return {
        ...turn,
        steps: turn.steps.map((step) =>
          step.callId === event.callId ? { ...step, endedAt: now, outcome: event.outcome } : step
        ),
      };
    case 'text_delta':
      return { ...turn, answerText: turn.answerText + event.text };
    case 'draft':
      return { ...turn, preparationId: event.preparationId };
    case 'done':
      return {
        ...turn,
        status: event.status,
        sources: event.sources,
        // The server measured the answer; the thread shows that duration.
        completedAt: new Date(Date.parse(turn.startedAt) + event.durationMs).toISOString(),
      };
  }
};

/** Folds one event into the turn the stream is answering (`turnRef`, pending or named). */
export function applyPanelEvent(
  conversation: CompanionPanelConversationDto,
  turnRef: string,
  event: CompanionPanelEvent,
  now: string
): CompanionPanelConversationDto {
  return {
    ...conversation,
    turns: conversation.turns.map((turn) =>
      turn.id === turnRef ? applyToTurn(turn, event, now) : turn
    ),
  };
}
